import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, errText, media } from "../../lib/api";
import { Badge, DataTable, Drawer, KeyRow, Panel, Select, TabBar, stamp, useTable } from "./ui";
import { DriverDetail } from "./DeliveryNetworkPage";

const STAGES = ["applied", "documents_requested", "documents_verified", "active", "rejected"];

const ApplicationDrawer = ({ id, onClose, onChanged }) => {
  const [a, setA] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const [active, archived] = await Promise.all([
        api.get("/admin/applications"),
        api.get("/admin/applications", { params: { archived: true } }),
      ]);
      setA([...active.data, ...archived.data].find((row) => row.id === id) || null);
    } catch (e) {
      setMsg(errText(e));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const act = async (fn, done) => {
    setMsg("");
    try {
      await fn();
      setMsg(done);
      await load();
      onChanged?.();
    } catch (e) {
      setMsg(errText(e));
    }
  };

  if (!a) return <Drawer testid="application-drawer" title="Application" onClose={onClose}><p className="text-sm text-muted">{msg || "Loading…"}</p></Drawer>;

  return (
    <Drawer testid="application-drawer" title={a.shop_name} subtitle={`${a.city_name} · ${a.stage}`} onClose={onClose}>
      {msg ? <p className="text-sm text-brass" data-testid="application-drawer-message">{msg}</p> : null}
      <Panel title="The shop">
        <KeyRow label="Owner">{a.owner_name}</KeyRow>
        <KeyRow label="Email">{a.email}</KeyRow>
        <KeyRow label="Phone">{a.phone}</KeyRow>
        <KeyRow label="Address">{a.address}</KeyRow>
        <KeyRow label="Experience">{a.years_in_business} years · {a.capacity_per_day} bouquets a day</KeyRow>
        <KeyRow label="Notes">{a.notes || "—"}</KeyRow>
        {a.review_note ? <KeyRow label="Our note">{a.review_note}</KeyRow> : null}
        {a.florist_id ? <KeyRow label="Shop record"><Link className="underline decoration-brass" to={`/admin/florists/${a.florist_id}`}>open the profile</Link></KeyRow> : null}
        <KeyRow label="Application №"><span className="tabular">{a.application_number || "—"}</span></KeyRow>
      </Panel>

      <Panel title="Bank">
        <KeyRow label="Bank">{a.bank?.bank_name || "—"}</KeyRow>
        <KeyRow label="Holder">{a.bank?.account_holder}</KeyRow>
        <KeyRow label="RIB">····{a.bank?.rib_last4 || "----"} {a.bank?.verified ? "✓" : ""}</KeyRow>
      </Panel>

      <Panel title="Documents" testid="application-documents">
        <p className="text-[11px] text-muted mb-2">Click the label to preview. Reject asks for a reason and e-mails the florist a re-upload link.</p>
        <div className="flex flex-wrap gap-2">
          {(a.documents || []).map((d) => (
            <div key={d.id} className="border border-brass-light p-2 text-[11px] space-y-1 w-52" data-testid={`app-doc-${d.id}`}>
              <a href={media(d.url)} target="_blank" rel="noreferrer" className="underline block truncate" title={d.label}>{d.label}</a>
              {/\.(jpe?g|png|webp)$/i.test(d.url) ? (
                <a href={media(d.url)} target="_blank" rel="noreferrer" className="block">
                  <img src={media(d.url)} alt={d.label} className="w-full h-24 object-cover border border-brass-light" loading="lazy" />
                </a>
              ) : (
                <a href={media(d.url)} target="_blank" rel="noreferrer" className="block w-full h-24 border border-dashed border-brass-light flex items-center justify-center text-muted">PDF · click to preview</a>
              )}
              {d.note ? <p className="text-danger text-[10px] break-words">Note : {d.note}</p> : null}
              <span className={d.status === "approved" ? "text-success" : d.status === "rejected" ? "text-danger" : "text-muted"}>{d.status}</span>
              <div className="flex gap-2">
                <button className="underline" data-testid={`doc-approve-${d.id}`}
                        onClick={() => act(() => api.post(`/admin/applications/${a.id}/documents`, { document_id: d.id, status: "approved" }), "Document approved.")}>approve</button>
                <button className="underline text-danger" data-testid={`doc-reject-${d.id}`}
                        onClick={() => {
                          const reason = window.prompt(`Why is « ${d.label} » being rejected?\nThe florist gets this message by e-mail with a re-upload link.`,
                                                        d.note || "Illisible, merci de recharger");
                          if (reason === null) return;
                          act(() => api.post(`/admin/applications/${a.id}/documents`,
                                              { document_id: d.id, status: "rejected", note: reason || "à recharger" }),
                              "Document rejected — e-mail sent.");
                        }}>reject</button>
              </div>
            </div>
          ))}
          {(a.documents || []).length === 0 ? <p className="text-sm text-muted">Nothing uploaded yet.</p> : null}
        </div>
      </Panel>

      <Panel title="Move the application">
        <div className="flex flex-wrap gap-2">
          {STAGES.slice(1).map((s) => (
            <button key={s} data-testid={`stage-${s}-${a.id}`} className={s === "active" ? "btn-primary text-xs py-2 px-3" : "btn-ghost text-[11px] py-2 px-3"}
                    onClick={() => act(() => api.post(`/admin/applications/${a.id}/stage`, { stage: s, note: s === "documents_requested" ? "Documents à compléter" : "" }), `Moved to ${s.replace("_", " ")}.`)}>
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </Panel>
    </Drawer>
  );
};

export default function ApplicationsPage() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "drivers" ? "drivers" : "florists";
  const setTab = (t) => {
    const n = Object.fromEntries(sp.entries());
    delete n.application;
    if (t === "drivers") n.tab = "drivers"; else delete n.tab;
    setSp(n, { replace: true });
  };
  return (
    <div data-testid="admin-applications-page">
      <h1 className="font-display text-2xl text-ink mb-1">Applications</h1>
      <p className="text-sm text-muted mb-4">Vet incoming florist shops and delivery drivers before they go live.</p>
      <div className="mb-4">
        <TabBar testid="applications-tabs" value={tab} onChange={setTab}
                tabs={[["florists", "Florist shops"], ["drivers", "Delivery drivers"]]} />
      </div>
      {tab === "florists" ? <FloristApplications /> : <DriverApplications />}
    </div>
  );
}

const DRIVER_STATUS = ["pending", "active", "suspended", "rejected"];

const DriverApplications = () => {
  const [status, setStatus] = useState("pending");
  const [type, setType] = useState("");
  const [rows, setRows] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/drivers", { params: { status, type } })
      .then((r) => setRows(r.data.drivers || []))
      .catch(() => setRows([]));
  }, [status, type]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="driver-applications">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted">Each driver needs a signed agreement plus CIN, licence and insurance on file. Open a driver to review documents and approve.</p>
        <div className="flex items-center gap-2">
          <Select testid="driver-filter-status" placeholder="Any status" value={status} onChange={setStatus} options={DRIVER_STATUS} />
          <Select testid="driver-filter-type" placeholder="Any type" value={type} onChange={setType} options={["network", "florist"]} />
        </div>
      </div>
      <div className="card divide-y divide-brass-light">
        {rows === null ? <p className="p-4 text-sm text-muted">Loading drivers…</p> : null}
        {rows && rows.length === 0 ? <p className="p-4 text-sm text-muted" data-testid="driver-applications-empty">No driver matches that.</p> : null}
        {(rows || []).map((d) => (
          <button key={d.id} data-testid={`driver-application-${d.id}`} onClick={() => setDetailId(d.id)}
                  className="w-full text-start p-3 flex items-center justify-between gap-3 hover:bg-plaster-deep transition-colors">
            <span className="min-w-0">
              <span className="block text-sm text-ink truncate">{d.name}</span>
              <span className="block text-xs text-muted truncate">{d.email} · {d.city_name} · {d.driver_type}</span>
            </span>
            <Badge tone={d.status === "active" ? "good" : d.status === "rejected" ? "bad" : "warn"}>{d.status}</Badge>
          </button>
        ))}
      </div>
      {detailId ? <DriverDetail id={detailId} onClose={() => setDetailId(null)} onChanged={load} /> : null}
    </div>
  );
};

const FloristApplications = () => {
  const table = useTable("/admin/tables/applications");
  const [sp, setSp] = useSearchParams();
  const [notifyMsg, setNotifyMsg] = useState("");
  const [notifyBusy, setNotifyBusy] = useState(false);
  const openId = sp.get("application");
  const close = () => { const n = Object.fromEntries(sp.entries()); delete n.application; setSp(n, { replace: true }); };
  const archived = !!table.params.archived;

  const notifyMissing = async () => {
    if (!window.confirm("E-mail every florist that still has missing or rejected documents?")) return;
    setNotifyBusy(true); setNotifyMsg("");
    try {
      const { data } = await api.post("/admin/applications/notify-missing-docs");
      setNotifyMsg(`Sent ${data.sent} reminder${data.sent === 1 ? "" : "s"} · ${data.already_complete} already complete.`);
    } catch (e) {
      setNotifyMsg(errText(e));
    } finally { setNotifyBusy(false); }
  };

  const columns = [
    { key: "application_number", label: "Ref", sortable: false, render: (r) => <span className="text-xs tabular whitespace-nowrap text-ink">{r.application_number || "—"}</span> },
    { key: "created_at", label: "Received", sortable: true, render: (r) => <span className="text-xs whitespace-nowrap">{stamp(r.created_at)}</span> },
    { key: "shop_name", label: "Shop", sortable: true, render: (r) => (
      <button className="underline decoration-brass underline-offset-2 text-start" data-testid={`application-open-${r.id}`}
              onClick={() => setSp({ ...Object.fromEntries(sp.entries()), application: r.id }, { replace: true })}>
        {r.shop_name}<span className="block text-xs text-muted">{r.owner_name}</span>
      </button>
    ) },
    { key: "city_name", label: "City" },
    { key: "stage", label: "Stage", sortable: true, render: (r) => <Badge tone={r.stage === "active" ? "good" : r.stage === "rejected" ? "bad" : "warn"}>{(r.stage || "").replace(/_/g, " ")}</Badge> },
    { key: "documents", label: "Documents", render: (r) => <span className="tabular text-xs">{r.documents_total} on file · {r.documents_pending} pending</span> },
    { key: "email", label: "Contact", render: (r) => <span className="text-xs">{r.email}<span className="block text-muted">{r.phone}</span></span> },
    { key: "years_in_business", label: "Years", render: (r) => <span className="tabular">{r.years_in_business ?? "—"}</span> },
    { key: "florist_id", label: "", render: (r) => (r.florist_id ? <Link className="btn-ghost text-[11px]" to={`/admin/florists/${r.florist_id}`}>Profile</Link> : "—") },
  ];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted">
          {archived
            ? "Archived applications. Rejected shops land here and stay accessible for follow-up."
            : "Every florist needs 4 documents on file (ICE · RC · CIN · RIB). Use the bulk button to nudge those with gaps."}
        </p>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs py-2 px-3" data-testid="applications-archive-toggle"
                  onClick={() => table.set({ archived: !archived })}>
            {archived ? "← Back to pipeline" : "View archive"}
          </button>
          {!archived ? (
            <button className="btn-ghost text-xs py-2 px-3" data-testid="applications-notify-missing"
                    onClick={notifyMissing} disabled={notifyBusy}>
              {notifyBusy ? "Sending…" : "Notify missing documents"}
            </button>
          ) : null}
        </div>
      </div>
      {notifyMsg ? <p className="text-xs text-brass mb-3" data-testid="applications-notify-message">{notifyMsg}</p> : null}
      <DataTable table={table} columns={columns} testid="applications-table" name="applications"
                 searchPlaceholder="Shop, owner, email…" empty="No application matches that."
                 filters={<Select testid="applications-filter-stage" placeholder="Any stage" value={table.params.stage}
                                  onChange={(v) => table.set({ stage: v })} options={STAGES} />} />
      {openId ? <ApplicationDrawer id={openId} onClose={close} onChanged={table.reload} /> : null}
    </>
  );
};
